import { getServiceUrl } from '../api.config';
import { ApiClientOptions } from '../types/api';
import ApiClient from '../utils/ApiClient';
import qs from '../utils/query';
import {
  isNotEmptyArray,
  isNotEmptyObject,
  isPositiveInteger,
  isTrue,
} from '../utils/validators';

export default class ServiceProductApi extends ApiClient {
  public constructor(options?: ApiClientOptions) {
    super(getServiceUrl('product'), options);
  }

  public getProductByKeyId(
    productKeyId: number,
    queries: { with_deleted_shares?: boolean } = {},
  ) {
    const url = `/product/v3/products/${productKeyId}`;
    return this.get(
      url,
      qs.stringify(queries, true),
      false, // Cache
    );
  }

  public getSharingunit(sharingunitID: number, queries: any) {
    const query = `/product/v2/sharingunit/${sharingunitID}`;
    return this.get(
      query,
      qs.stringify(queries, true),
      false, // Cache
    );
  }

  public saveProductV3(payload: any) {
    const query = `/product/v3/products/save`;

    const data: any = {};
    // Validate params
    if (isNotEmptyArray(payload.breakingChanges)) {
      data.breaking_changes = payload.breakingChanges;
    }
    if (isNotEmptyObject(payload.product)) {
      data.product = payload.product;
    }
    if (isTrue(payload.patch)) {
      data.patch = true;
    }
    if (isNotEmptyArray(payload.deepPatchAttributes)) {
      data.deep_patch_attributes = payload.deepPatchAttributes;
    }

    return this.post(query, data);
  }

  public shareProduct(productId: number, payload: any) {
    const query = `/product/v2/products/${productId}/share`;

    const data: any = {
      with_sharingunits: true,
    };
    // Validate params
    if (isNotEmptyArray(payload.targetOrganizations)) {
      data.target_organizations = payload.targetOrganizations;
    }
    if (isPositiveInteger(payload.organizationId)) {
      data.organization_id = payload.organizationId;
    }

    return this.post(query, data);
  }

  public getBreakingChanges(productId: number, data: any) {
    const query = `/product/v2/products/${productId}/breakingchanges`;
    return this.post(query, data);
  }

  public checkProduct(data: any) {
    const query = '/product/v2/products/check';
    return this.post(query, data);
  }

  public checkProductWithHierarchy(data: any) {
    const query = '/product/v2/products/check/hierarchy';
    return this.post(query, data);
  }

  public acceptProduct(
    productId: number,
    organizationId: number,
    transactionIds: number[],
    reason: any,
    targetmarketId: number,
  ) {
    const data: any = {
      organization_id: organizationId,
      product_id: productId,
      reason,
      target_market_id: targetmarketId,
      transaction_ids: transactionIds,
    };
    const query = '/product/v2/products/accept';
    return this.post(query, data);
  }

  public acceptProductById(
    productId: number,
    organizationId: number,
    targetMarketId: number,
    reason: any,
  ) {
    const data: any = {
      organization_id: organizationId,
      reason,
      target_market_id: targetMarketId,
    };
    const query = `/product/v2/products/${productId}/accept'`;
    return this.post(query, data);
  }

  public bulkAcceptProducts(
    productIds: number[],
    organizationId: number,
    targetMarketId: number,
    reason: any,
  ) {
    const data: any = {
      organization_id: organizationId,
      product_ids: productIds,
      reason,
      target_market_id: targetMarketId,
    };
    const query = '/product/v2/products/accept/bulk';
    return this.post(query, data);
  }

  public refuseProduct(
    productId: number,
    organizationId: number,
    transactionIds: number[],
    reason: any,
    targetmarketId: number,
  ) {
    const data = {
      organization_id: organizationId,
      product_id: productId,
      reason,
      target_market_id: targetmarketId,
      transaction_ids: transactionIds,
    };
    const query = '/product/v2/products/refuse';
    return this.post(query, data);
  }

  public getHistoryByKeyId(productKeyId: number, queries: any) {
    const query = `/product/v3/products/${productKeyId}/history`;
    return this.get(
      query,
      qs.stringify(queries, true),
      false, // Cache
    );
  }

  public getStatusHistoryByKeyId(productKeyId: number, queries: any) {
    const query = `/product/v3/products/${productKeyId}/sharing_status_history`;
    return this.get(
      query,
      qs.stringify(queries, true),
      false, // Cache
    );
  }

  public acceptImport(
    transactionIds: number[],
    organizationId: number,
    reason: any,
    filters: any,
  ) {
    const data = {
      filters,
      organization_id: organizationId,
      reason,
      transaction_ids: transactionIds,
    };
    const query = `/product/v2/imports/accept`;
    return this.post(query, data);
  }

  public createProduct(
    reference: string,
    targetMarketId: number,
    reason: any,
    data: any,
    message?: string,
    forceUpdate?: boolean,
    identifier?: string,
  ) {
    return this.post('/product/v2/products/create', {
      product: { data },
      reason,
      message,
      reference,
      target_market_id: targetMarketId,
      force_update: forceUpdate,
      identifier,
    });
  }

  public duplicateProduct(
    reference: string,
    targetMarketId: number,
    reason: any,
    sourceProductId: number,
    data: any,
  ) {
    return this.post('/product/v2/products/duplicate', {
      product: { data },
      reason,
      reference,
      source_product_id: sourceProductId,
      target_market_id: targetMarketId,
    });
  }

  public declineProduct(
    reference: string,
    targetMarketId: number,
    reason: number,
    message?: string,
    productIdentifier?: string,
  ) {
    return this.post('/product/v2/products/decline', {
      reason,
      reference,
      target_market_id: targetMarketId,
      message,
      product_identifier: productIdentifier,
    });
  }

  public createSharingUnit(sharingUnit: any) {
    const query = '/product/v2/sharingunit';
    return this.post(query, sharingUnit);
  }

  public bulkCreateSharingUnit(sharingUnits: any[]) {
    const query = '/product/v3/sharing_units/upsert';
    const body: any = { sharing_units: sharingUnits };
    return this.post(query, body);
  }

  public bulkCreateSharingUnitWithTarget(sharingUnits: any[]) {
    const query = '/product/v4/sharing_units/upsert';
    const body: any = { sharing_units: sharingUnits };
    return this.post(query, body);
  }

  public tagVersion(product_key_id: number, tag: string) {
    const query = `/product/v3/products/save/tag`;
    return this.post(query, {
      product_key_ids: [product_key_id],
      tag,
      remove: false,
    });
  }

  public unTagVersion(product_key_id: number, tag: string) {
    const query = `/product/v3/products/save/tag`;
    return this.post(query, {
      product_key_ids: [product_key_id],
      tag,
      remove: true,
    });
  }

  public unsynchronizeVersion(
    productKeyIds: number[],
    targetOrganizationIds: number[],
  ) {
    const query = `/product/v2/products/unsynchronize`;
    return this.post(query, {
      product_key_ids: productKeyIds,
      target_organization_ids: targetOrganizationIds,
    });
  }

  public unshareVersion(
    productKeyIds: number[],
    targetOrganizationIds: number[],
  ) {
    const query = `/product/v2/products/unsynchronize`;
    return this.post(query, {
      product_key_ids: productKeyIds,
      target_organization_ids: targetOrganizationIds,
      full: true,
    });
  }

  public disableFromActiveRange(productKeyIds: number[]) {
    const query = '/product/v2/products/active_range/disable';
    const body: any = { product_key_ids: productKeyIds };
    return this.post(query, body);
  }

  public getSharableHierarchies(
    productKeyIds: number[],
    targetOrganizationId: number,
  ) {
    const query = '/product/v2/hierarchies/sharable';
    const body: any = {
      product_key_ids: productKeyIds,
      target_organization_id: targetOrganizationId,
    };
    return this.post(query, body);
  }

  public setExportableTag(productKeyIds: number[]) {
    const query = '/product/v3/products/set/exportable/tag';
    // when user ask for putting the exportable tag on a product
    // it's always requesting replacement
    // backend will decide whether the context commands to alter other products as well
    // setting sreplace == false would be to forbid this replacement
    const body: any = { product_key_ids: productKeyIds, replace: true };
    return this.post(query, body);
  }
}
